// Propriedades das linhas de banco de dados: como cada tipo aparece e os
// editores (popovers) para mudar o valor.

import * as store from "./store.js";
import { el, popup, fmtDate, fmtDateTime, COLORS, titleOf, icon } from "./dom.js";
import { iconNode } from "./icons.js";
import { avatar } from "./people.js";

export const TYPES = [
  ["text", "Texto", "≡"], ["number", "Número", "#"], ["select", "Seleção", "◉"], ["multi_select", "Seleção múltipla", "☰"],
  ["date", "Data", "📅"], ["person", "Pessoa", "👤"], ["checkbox", "Caixa de seleção", "☑"], ["url", "Link", "🔗"],
  ["email", "E-mail", "@"], ["relation", "Relação", "↗"], ["lookup", "Consulta (lookup)", "⤷"], ["rollup", "Agregação (rollup)", "Σ"],
  ["id", "ID automático", "№"], ["created_time", "Criado em", "🕒"], ["updated_time", "Atualizado em", "🕒"],
];
export const typeLabel = (t) => (TYPES.find((x) => x[0] === t) || [t, t])[1];
export const typeIcon = (t) => (TYPES.find((x) => x[0] === t) || [t, t, "≡"])[2];

export const COMPUTED = ["id", "lookup", "rollup", "created_time", "updated_time"];
export const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

export function valueOf(prop, row) {
  if (prop.id === "title") return row.title;
  if (prop.type === "id") return row.seq ? `${prop.prefix || ""}${prop.prefix ? "-" : ""}${row.seq}` : "";
  if (prop.type === "created_time") return row.created_at;
  if (prop.type === "updated_time") return row.updated_at;
  if (row.computed && prop.id in row.computed) return row.computed[prop.id];
  return row.props?.[prop.id];
}

// Títulos de linhas de outros bancos (para relações): vem das linhas em cache.
const loadingDbs = new Set();
const lockedDbs = new Set(); // bancos ligados que a pessoa não alcança
export function relatedRow(dbId, rowId) {
  const db = store.cached(dbId);
  if (db?.rows) return db.rows.find((r) => r.id === rowId) || null;
  if (dbId && !loadingDbs.has(dbId) && !lockedDbs.has(dbId)) {
    loadingDbs.add(dbId);
    store.load(dbId).then(() => store.emit("rows", dbId))
      .catch(() => { lockedDbs.add(dbId); store.emit("rows", dbId); })
      .finally(() => setTimeout(() => loadingDbs.delete(dbId), 5000));
  }
  return null;
}

// Como no Notion: o que vem de um ambiente ou página sem acesso fica bloqueado.
export function lockedChip(text = "Sem acesso") {
  return el("span", { class: "rel-chip locked", title: "Você não tem acesso a esta página" }, el("span", { class: "rel-icon" }, "🔒"), text);
}

export function chip(name, color) {
  return el("span", { class: `chip c-${color || "default"}` }, name);
}

export function optionColor(prop, name) {
  return (prop.options || []).find((o) => o.name === name)?.color || "default";
}

// Valor de exibição (elemento) ou null quando vazio.
export function display(prop, row, ctx, { compact = false } = {}) {
  const v = valueOf(prop, row);
  if (isEmpty(v) && prop.type !== "checkbox") return row.locked?.[prop.id] ? lockedChip() : null;
  switch (prop.type) {
    case "select":
    case "status":
      return chip(v, optionColor(prop, v));
    case "multi_select":
      return el("span", { class: "chips" }, [].concat(v).map((x) => chip(x, optionColor(prop, x))));
    case "person":
      return el("span", { class: "chips" }, [].concat(v).map((id) => {
        const name = (ctx.users || store.state.users).find((u) => u.id === id)?.name || "Alguém";
        return el("span", { class: "person" }, avatar({ id, name }, "sm"), compact ? null : name);
      }));
    case "checkbox":
      return compact && !v ? null : el("span", { class: "checkbox-view" + (v ? " on" : "") }, v ? "✓" : "");
    case "date":
      return el("span", {}, fmtDate(v));
    case "created_time":
    case "updated_time":
      return el("span", { class: "muted" }, fmtDateTime(v));
    case "url":
      return el("a", { href: v, class: "link-value", target: "_blank", onclick: (e) => e.stopPropagation() }, v.replace(/^https?:\/\//, ""));
    case "email":
      return el("a", { href: `mailto:${v}`, class: "link-value", onclick: (e) => e.stopPropagation() }, v);
    case "relation":
      return el("span", { class: "chips" }, [].concat(v).map((id) => {
        // O servidor diz o que a pessoa alcança; sem acesso, nem o título aparece.
        const known = row.rel?.[prop.id];
        if ((known && id in known && known[id] === null) || (!known && lockedDbs.has(prop.database))) return lockedChip();
        const r = (known && known[id]) || relatedRow(prop.database, id);
        return el("span", {
          class: "rel-chip",
          title: "Abrir",
          onclick: (e) => { e.stopPropagation(); ctx.openRow?.(id); },
        }, el("span", { class: "rel-icon" }, iconNode(r?.icon, "↗")), r ? titleOf(r) : "…");
      }));
    case "lookup": {
      const target = targetProp(prop);
      return el("span", { class: "chips" }, ...(row.locked?.[prop.id] ? [lockedChip()] : []), [].concat(v).map((x) => {
        if (target && ["select", "status"].includes(target.type)) return chip(x, optionColor(target, x));
        if (x === true) return "✓";
        if (x === false) return "—";
        if (target?.type === "date") return el("span", { class: "plain" }, fmtDate(x));
        return el("span", { class: "plain" }, Array.isArray(x) ? x.join(", ") : String(x));
      }));
    }
    case "rollup":
      return el("span", { class: "num" }, String(v), row.locked?.[prop.id] ? lockedChip() : null);
    case "number":
      return el("span", { class: "num" }, Number(v).toLocaleString("pt-BR"));
    default:
      return el("span", { class: "text-value" }, String(v));
  }
}

// Propriedade de destino de uma consulta (lookup/rollup), se o banco estiver em cache.
function targetProp(prop) {
  const db = ctxDbOf(prop);
  return db?.schema?.properties?.find((p) => p.id === prop.target) || null;
}
function ctxDbOf(prop) {
  // A relação usada pela consulta aponta para o banco de destino.
  const owner = [...store.state.tree.values()].find((d) => d.kind === "database" && store.cached(d.id)?.page.schema?.properties?.some((p) => p === prop));
  const rel = owner && store.cached(owner.id).page.schema.properties.find((p) => p.id === prop.relation);
  return rel ? store.cached(rel.database)?.page : null;
}

// ---------- Editores ----------

// Abre o editor de uma propriedade perto de `anchor`. set(value) grava.
export function edit(anchor, prop, row, ctx, set) {
  const v = valueOf(prop, row);
  switch (prop.type) {
    case "checkbox":
      return set(!v);
    case "select":
    case "status":
      return optionPicker(anchor, prop, v ? [v] : [], false, ctx, (vals) => set(vals[0] ?? null));
    case "multi_select":
      return optionPicker(anchor, prop, v || [], true, ctx, (vals) => set(vals));
    case "person":
      return personPicker(anchor, v || [], ctx, set);
    case "relation":
      return relationPicker(anchor, prop, v || [], ctx, set);
    case "date":
      return datePicker(anchor, v, set);
    default:
      if (COMPUTED.includes(prop.type)) return null;
      return textEditor(anchor, prop, v, set);
  }
}

function textEditor(anchor, prop, value, set) {
  const multi = prop.type === "text" || prop.id === "title";
  const input = multi
    ? el("textarea", { class: "pop-input", rows: 3 })
    : el("input", { class: "pop-input", type: prop.type === "number" ? "number" : prop.type === "url" ? "url" : prop.type === "email" ? "email" : "text" });
  input.value = value ?? "";
  let done = false;
  const commit = () => {
    if (done) return;
    done = true;
    let v = input.value;
    if (prop.type === "number") v = v === "" ? null : Number(v);
    else v = v.trim() === "" ? null : v;
    if (JSON.stringify(v ?? null) !== JSON.stringify(value ?? null)) set(v);
  };
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !(multi && e.shiftKey)) { e.preventDefault(); box.close(); }
  });
  const box = popup(anchor, el("div", { class: "pop-edit" }, input,
    multi ? el("div", { class: "pop-hint" }, "Enter salva · Shift+Enter quebra linha") : null), { onClose: commit });
  requestAnimationFrame(() => { input.focus(); input.select?.(); });
  return box;
}

function datePicker(anchor, value, set) {
  const input = el("input", { class: "pop-input", type: "date", value: value || "" });
  const box = popup(anchor, el("div", { class: "pop-edit" }, input,
    el("div", { class: "pop-row" },
      el("button", { class: "link", onclick: () => { input.value = new Date().toISOString().slice(0, 10); box.close(); } }, "Hoje"),
      el("button", { class: "link", onclick: () => { input.value = ""; box.close(); } }, "Limpar"))),
  { onClose: () => { if ((input.value || null) !== (value || null)) set(input.value || null); } });
  requestAnimationFrame(() => input.focus());
  return box;
}

function optionPicker(anchor, prop, selected, multi, ctx, set) {
  const chosen = new Set(selected);
  const filter = el("input", { class: "pop-input", placeholder: multi ? "Procure ou crie opções" : "Procure ou crie uma opção" });
  const list = el("div", { class: "pick-list" });
  let index = 0;
  const visible = () => {
    const q = filter.value.trim().toLowerCase();
    return (prop.options || []).filter((o) => !q || o.name.toLowerCase().includes(q));
  };
  const draw = () => {
    const opts = visible();
    const q = filter.value.trim();
    const exact = (prop.options || []).some((o) => o.name.toLowerCase() === q.toLowerCase());
    list.replaceChildren(
      ...opts.map((o, i) => el("button", {
        class: "pick-row" + (i === index ? " active" : "") + (chosen.has(o.name) ? " on" : ""),
        onmousedown: (e) => e.preventDefault(),
        onclick: () => choose(o.name),
      }, multi ? el("span", { class: "check" }, chosen.has(o.name) ? "✓" : "") : null, chip(o.name, o.color),
      !multi && chosen.has(o.name) ? el("span", { class: "check right" }, "✓") : null)),
      q && !exact && ctx.canEditSchema
        ? el("button", { class: "pick-row create" + (index === opts.length ? " active" : ""), onclick: () => createOption(q) }, "Criar ", chip(q, "default"))
        : null,
      !opts.length && !q ? el("p", { class: "pick-empty" }, "Nenhuma opção. Digite para criar.") : null,
      chosen.size && !multi ? el("button", { class: "pick-row muted", onclick: () => { chosen.clear(); set([]); box.close(); } }, "Limpar") : null);
  };
  const choose = (name) => {
    if (multi) {
      if (chosen.has(name)) chosen.delete(name); else chosen.add(name);
      set([...chosen]);
      draw();
    } else {
      set([name]);
      box.close();
    }
  };
  const createOption = async (name) => {
    const color = COLORS[1 + ((prop.options || []).length % (COLORS.length - 1))];
    prop.options = [...(prop.options || []), { name, color }];
    await ctx.saveSchema?.();
    filter.value = "";
    choose(name);
  };
  filter.addEventListener("input", () => { index = 0; draw(); });
  filter.addEventListener("keydown", (e) => {
    const opts = visible();
    const max = opts.length + (filter.value.trim() ? 1 : 0);
    if (e.key === "ArrowDown") { e.preventDefault(); index = Math.min(max - 1, index + 1); draw(); }
    if (e.key === "ArrowUp") { e.preventDefault(); index = Math.max(0, index - 1); draw(); }
    if (e.key === "Enter") {
      e.preventDefault();
      if (opts[index]) choose(opts[index].name);
      else if (filter.value.trim() && ctx.canEditSchema) createOption(filter.value.trim());
    }
  });
  const box = popup(anchor, el("div", { class: "pop-pick" }, filter, list));
  draw();
  requestAnimationFrame(() => filter.focus());
  return box;
}

function personPicker(anchor, selected, ctx, set) {
  const chosen = new Set(selected);
  const users = ctx.users?.length ? ctx.users : store.state.users;
  const list = el("div", { class: "pick-list" });
  const draw = () => list.replaceChildren(...users.map((u) => el("button", {
    class: "pick-row" + (chosen.has(u.id) ? " on" : ""),
    onclick: () => {
      if (chosen.has(u.id)) chosen.delete(u.id); else chosen.add(u.id);
      set([...chosen]);
      draw();
    },
  }, el("span", { class: "check" }, chosen.has(u.id) ? "✓" : ""), avatar(u, "sm"), u.name)),
  users.length ? null : el("p", { class: "pick-empty" }, "Compartilhe a página com alguém para poder escolher pessoas."));
  draw();
  return popup(anchor, el("div", { class: "pop-pick" }, list));
}

function relationPicker(anchor, prop, selected, ctx, set) {
  const chosen = [...selected];
  const filter = el("input", { class: "pop-input", placeholder: "Procure uma página para ligar" });
  const list = el("div", { class: "pick-list" });
  let rows = store.cached(prop.database)?.rows || null;
  const dbTitle = store.node(prop.database)?.title || "banco ligado";
  const draw = () => {
    if (!rows) {
      list.replaceChildren(el("p", { class: "pick-empty" }, "Carregando…"));
      return;
    }
    const q = filter.value.trim().toLowerCase();
    const linked = chosen.map((id) => rows.find((r) => r.id === id) || { id, title: "(sem acesso)" });
    const others = rows.filter((r) => !chosen.includes(r.id) && (!q || (r.title || "").toLowerCase().includes(q))).slice(0, 60);
    list.replaceChildren(
      linked.length ? el("div", { class: "pick-label" }, "Ligadas") : null,
      ...linked.map((r) => el("div", { class: "pick-row on" },
        el("span", { class: "rel-icon" }, iconNode(r.icon, "↗")), el("span", { class: "grow" }, titleOf(r)),
        el("button", { class: "icon-btn tiny", title: "Desligar", onclick: () => { chosen.splice(chosen.indexOf(r.id), 1); set([...chosen]); draw(); } }, "×"))),
      el("div", { class: "pick-label" }, `Em ${dbTitle}`),
      ...others.map((r) => el("button", { class: "pick-row", onclick: () => { chosen.push(r.id); set([...chosen]); filter.value = ""; draw(); } },
        el("span", { class: "rel-icon" }, iconNode(r.icon, "↗")), titleOf(r))),
      others.length ? null : el("p", { class: "pick-empty" }, q ? "Nada encontrado." : "Nenhuma outra linha."));
  };
  filter.addEventListener("input", draw);
  const box = popup(anchor, el("div", { class: "pop-pick wide" }, filter, list));
  draw();
  if (!rows) {
    store.load(prop.database).then((d) => { rows = d.rows || []; draw(); })
      .catch(() => { list.replaceChildren(el("p", { class: "pick-empty" }, "Você não tem acesso ao banco ligado.")); });
  }
  requestAnimationFrame(() => filter.focus());
  return box;
}

// ---------- Painel de propriedades (linha aberta) ----------

export function panel(row, db, ctx) {
  const props = (db?.schema?.properties || []);
  const box = el("section", { class: "props-panel" });
  const draw = () => {
    box.replaceChildren(...props.map((p) => {
      const value = el("div", { class: "prop-value" + (ctx.canEdit && !COMPUTED.includes(p.type) ? " editable" : "") });
      const fill = () => value.replaceChildren(display(p, row, ctx) || el("span", { class: "empty" }, ctx.canEdit && !COMPUTED.includes(p.type) ? "Vazio" : "—"));
      fill();
      if (ctx.canEdit && !COMPUTED.includes(p.type)) {
        value.addEventListener("click", (e) => {
          if (e.target.closest("a,.rel-chip")) return;
          edit(value, p, row, ctx, (v) => { ctx.setProp(row, p, v); fill(); });
        });
      }
      return el("div", { class: "prop-row" },
        el("div", { class: "prop-name", title: typeLabel(p.type) }, el("span", { class: "prop-icon" }, typeIcon(p.type)), p.name),
        value);
    }),
    ctx.canEdit ? el("button", { class: "link add-prop", onclick: () => ctx.openSettings?.() }, "+ Adicionar ou editar propriedades") : null);
  };
  draw();
  box.redraw = draw;
  return box;
}

export { icon };
